/*
 * 维护单多人协同同步 + 离线暂存（与 wechat_web 中的同名文件保持一致）
 *
 * 保存：每改一项只保存这一项（sync_patch 按路径合并写入，如 main_step.2.sub_step.3.n1），
 *       输入类控件停止输入约 1 秒或失焦时保存，选择类控件立即保存
 * 队列：所有写操作（保存一项 / 开关 / 结束步骤）按操作顺序进入发送队列，逐条发送；
 *       没网时队列暂存在本机（localStorage，照片在 IndexedDB），页面关掉再打开也不丢，
 *       联网后（网络恢复 / 任一请求成功 / 切回页面 / 定时探测）自动按顺序补传
 * 拉取：页面在前台时每 15 秒、切回页面时、点任何开关之前，拉一次服务器最新数据（GetHistoryVariables）
 * 合并：页面显示 = 服务器最新数据 + 本机还没上传成功的改动（含离线暂存、正在识别/打卡中的项）。
 *       每次拉取都以服务器为准纠正本地（本地被任何原因改乱时 15 秒内自愈），只有本机未上传的项不动；
 *       另以"上次看到的服务器数据"判断哪些是别人新改的，用于提示"xxx 更新了"和冲突提示
 *       平台改写表单值（草稿恢复、待办刷新）不会覆盖这些字段，见 core.js MW.start
 * 保护：正在输入 / 输入法拼字 / 拍照或上传中 / 弹窗打开 / 请求未返回时暂停刷新，结束后再补一次
 * 重画：只有检查清单变化时只重画有变化的步骤卡片
 */
export default function installSync(MW, host) {
  var util = MW.util, store = MW.store, api = MW.api, h = MW.h, offline = MW.offline;
  var POLL_MS = 15000;
  var TYPE_MS = 1000;
  var IDLE_MS = 1200;
  var RETRY_MS = 5000;
  var PROBE_MS = 10000;
  var META = "_sync_meta";
  var EXTRA_KEYS = [META, "qualification_msg", "sub_start"];
  /** 只在本机补默认值、服务器为空也不纠正的字段（处理人默认取当前用户 / 界面状态） */
  var LOCAL_DEFAULT = { face_user: 1, face_user_end: 1, sub_start: 1 };
  /** 可以拆到"项"一级合并的路径（其余字段整体比较） */
  var GRANULAR_RE = /^main_step(\.\d+(\.sub_step(\.\d+)?)?)?$/;
  var SUB_META_RE = /^(main_step\.\d+\.sub_step\.\d+)\./;
  var LABELS = {
    confirm_abnormal: "确认前次维护详情", sub_ticket_check_in: "打卡", check_in_dc: "打卡", face_photo: "维护开始前人脸识别",
    face_detect_result: "维护开始前人脸识别", scan_qr_code: "设备二维码", scan_code_result: "设备二维码", upload_start: "设备照片",
    sw_start: "开始维护", txt_start: "开始维护", upload_end: "异常项照片", other_matter: "其它问题记录", item_table: "备件耗材使用记录",
    item_table_temp: "备件耗材使用记录", maintain_summary: "维护总结", sw_end: "确认完成", txt_end: "确认完成", abnormal_confirm: "异常项数量确认",
    face_photo_end: "维护结束前人脸识别", face_detect_result_end: "维护结束前人脸识别", confirm: "确认正常结束",
  };

  var sync = (MW.sync = {
    snap: {}, // 上次看到的服务器数据（三方合并基准）
    meta: {}, // 各项最后修改人 { path: [user, time] }
    on: false,
    stopped: false,
  });

  var dirty = {}; // path -> 本地版本号（改了还没保存成功）
  var ver = {};
  var queue = {}; // 防抖中、还没进入发送队列的项 path -> { value? }
  var timers = {};
  var inflight = 0;
  var seq = 0; // 每发起一次写请求 +1，用于丢弃写之前发出的拉取结果
  var pulling = null;
  var wantPull = false;
  var idleTimer = null, pollTimer = null, retryTimer = null;
  var composing = false;
  var lastInput = 0;
  var pickUntil = 0;
  var lastSync = "";
  var failed = false;
  MW.uploading = 0;

  /* 发送队列（离线暂存） */
  var outbox = []; // [{ id, type: save|commit|end, patches:[{path,value,base}], guard, index, time, user, t, v:{path:ver} }]
  var waiters = {}; // id -> [{resolve, reject}]（仅本次打开有效）
  var done = {}; // 已补传的离线照片 localId -> 正式文件项（null 表示本机已丢失）
  var running = null;
  var draining = null;
  var wasOff = false; // 本轮队列中有过网络失败（用于恢复后汇总提示）
  var replayed = 0;
  var rejectedList = [];
  var lostPhotos = 0;
  var persistWarned = false;
  var offToastAt = 0;
  var noop = function () {};

  /* ---------------- 工具 ---------------- */
  function eq(a, b) {
    if (a === b) return true;
    return JSON.stringify(a === undefined ? null : a) === JSON.stringify(b === undefined ? null : b);
  }
  function isObj(v) { return v !== null && typeof v === "object"; }
  function parts(path) { return String(path).split("."); }
  function getIn(root, path) {
    var n = root;
    var ps = parts(path);
    for (var i = 0; i < ps.length; i++) {
      if (!isObj(n)) return undefined;
      n = n[ps[i]];
    }
    return n;
  }
  function setIn(root, path, value) {
    var ps = parts(path), n = root;
    for (var i = 0; i < ps.length - 1; i++) {
      if (!isObj(n[ps[i]])) {
        if (Array.isArray(n)) return false;
        n[ps[i]] = {};
      }
      n = n[ps[i]];
    }
    var last = ps[ps.length - 1];
    if (Array.isArray(n) && !(Number(last) >= 0 && Number(last) < n.length)) return false;
    n[last] = value;
    return true;
  }
  function metaKey(path) {
    var m = SUB_META_RE.exec(path);
    return m ? m[1] : path;
  }
  function me() { return (MW.biz && MW.biz.user()) || ""; }
  function hhmm(t) { return String(t || "").slice(11, 16); }
  /** path 本身、祖先或子孙中有本地未保存的改动（含 hold 中的字段） */
  function localPaths() { return Object.keys(dirty).concat(Object.keys(held)); }
  function dirtyUnder(path) {
    var p = path + ".";
    return localPaths().some(function (k) { return k === path || k.indexOf(p) === 0 || path.indexOf(k + ".") === 0; });
  }
  function dirtySelfOrAncestor(path) {
    return localPaths().some(function (k) { return k === path || path.indexOf(k + ".") === 0; });
  }
  /**
   * 暂时以本机为准的字段（人脸识别中、打卡中等：本机先显示过程状态，结果出来后再保存）。
   * 返回释放函数；释放前拉取到的服务器数据不会覆盖这些字段
   */
  var held = {};
  sync.hold = function (keys) {
    keys.forEach(function (k) { held[k] = (held[k] || 0) + 1; });
    var on = true;
    return function () {
      if (!on) return;
      on = false;
      keys.forEach(function (k) { if (!--held[k]) delete held[k]; });
    };
  };
  // 工单已结束：停止同步；无操作权限：照常拉取他人进度，但不写入
  function closed() { return MW.biz ? MW.biz.isEnded() : false; }
  function readonlyNow() { return MW.biz ? MW.biz.isClosed() : false; }
  function offlineNow() { return MW.net.offline; }

  /** 人类可读的位置：步骤 2 / 步骤 2 第 3 项 / 开始维护 */
  sync.label = function (path) {
    var m = /^main_step\.(\d+)(?:\.sub_step\.(\d+))?/.exec(path);
    if (m) {
      var ms = (store.get("main_step") || [])[+m[1]] || {};
      var sn = ms.step_sn || +m[1] + 1;
      if (m[2] == null) return "步骤 " + sn;
      var s = (ms.sub_step || [])[+m[2]] || {};
      return "步骤 " + sn + "." + (s.sub_step_sn || +m[2] + 1) + (s.t1 ? "「" + String(s.t1).trim() + "」" : "");
    }
    var top = parts(path)[0];
    var ml = MW.biz && MW.biz.mid ? MW.biz.mid.labels : null;
    if (ml && ml[top]) return typeof ml[top] === "function" ? ml[top](path) : ml[top];
    return LABELS[top] || "表单内容";
  };
  /** 某一项最后修改人 */
  sync.who = function (path) {
    var m = sync.meta[metaKey(path)] || sync.meta[path];
    return Array.isArray(m) ? { user: m[0] || "", time: m[1] || "" } : { user: "", time: "" };
  };

  /* ---------------- 忙碌判断：这些状态下暂停刷新 ---------------- */
  function inScope(el) {
    return (MW.partList || []).some(function (p) { return p.el && p.el.contains(el); }) ||
      (MW.floating || []).some(function (f) { return f.contains(el); });
  }
  sync.busy = function () {
    if (composing) return "输入中";
    var ae = document.activeElement;
    if (ae && ae !== document.body && /^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName) && !/^(file|radio|checkbox|button)$/.test(ae.type) && inScope(ae)) return "输入中";
    if (ae && ae.isContentEditable && inScope(ae)) return "输入中";
    if (Date.now() - lastInput < IDLE_MS) return "输入中";
    if (MW.uploading > 0 || Date.now() < pickUntil) return "上传中";
    if (document.querySelector(".mw-mask") || document.body.classList.contains("mw-noscroll")) return "弹窗中";
    if (inflight > 0) return "保存中";
    return "";
  };
  /** 选择文件 / 调起相机期间暂停（返回页面或选好文件后恢复） */
  sync.holdPick = function () { pickUntil = Date.now() + 60000; };
  sync.releasePick = function () { pickUntil = 0; whenIdle(); };

  /* ---------------- 保存 ---------------- */
  function markDirty(path) {
    ver[path] = (ver[path] || 0) + 1;
    dirty[path] = ver[path];
  }
  /**
   * 保存一项：opt.delay 防抖毫秒（输入类 1000，选择类 0）；opt.value 指定写入值（默认取当前表单值）
   * 没网时同样返回成功（已暂存在本机），不会抛错
   */
  sync.save = function (path, opt) {
    opt = opt || {};
    if (sync.stopped || readonlyNow()) return Promise.resolve();
    markDirty(path);
    queue[path] = "value" in opt ? { value: util.clone(opt.value) } : {};
    clearTimeout(timers[path]);
    var delay = opt.delay == null ? 0 : opt.delay;
    var p = new Promise(function (resolve) {
      timers[path] = setTimeout(function () {
        delete timers[path];
        send([path]).then(resolve, resolve);
      }, delay);
    });
    status();
    return p;
  };
  /** 输入过程中：只标记为本地未保存，并在停止输入约 1 秒后保存 */
  sync.typing = function (path) {
    lastInput = Date.now();
    return sync.save(path, { delay: TYPE_MS });
  };
  /** 立即把防抖中的项放进发送队列并发送（失焦、切后台、点开关前）；没网时不等待 */
  sync.flush = function () {
    var paths = Object.keys(timers);
    paths.forEach(function (p) { clearTimeout(timers[p]); delete timers[p]; });
    if (paths.length) send(paths).catch(noop);
    if (offlineNow()) return Promise.resolve();
    var waitInflight = function () {
      return inflight ? util.delay(120).then(waitInflight) : Promise.resolve();
    };
    return kick().then(waitInflight, waitInflight);
  };
  /** 待上传的操作数（含防抖中的项），提交前需为 0 */
  sync.pending = function () {
    var n = 0;
    outbox.forEach(function (e) { n += e.type === "end" ? 1 : (e.patches || []).length; });
    return n + Object.keys(queue).length;
  };
  /** 是否离线中 */
  sync.offline = function () { return offlineNow(); };

  function buildPatches(paths) {
    return paths.filter(function (p) { return queue[p]; }).map(function (p) {
      var q = queue[p];
      delete queue[p];
      var value = "value" in q ? q.value : util.clone(getIn(store.values, p));
      var base = util.clone(getIn(sync.snap, p));
      return { path: p, value: value === undefined ? null : value, base: base === undefined ? null : base };
    });
  }

  function request(method, args) {
    inflight++;
    seq++;
    status();
    return api.call(method, args).then(function (resp) {
      var r = resp && resp.data ? resp.data.result : null;
      if (r && r.code === -2) {
        // 无操作权限（已转派他人 / 非处理人）：转为只读，丢弃该操作
        if (MW.biz && MW.biz.permDenied) MW.biz.permDenied(r.msg);
        var pe = new Error(r.msg || "无操作权限");
        pe.drop = true;
        pe.perm = true;
        throw pe;
      }
      if (r && r.code === -1) {
        if (/已被他人提交/.test(r.msg || "")) stop(r.msg);
        var be = new Error(r.msg || "保存失败");
        be.drop = true; // 业务拒绝：重试也不会成功，丢弃该操作
        throw be;
      }
      if (!r || typeof r !== "object") throw new Error((resp && (resp.msg || resp.message || resp.Details)) || "保存失败");
      return r;
    }).then(function (r) {
      inflight--;
      failed = false;
      if (r.meta && isObj(r.meta)) sync.meta = r.meta;
      lastSync = r.time || util.now();
      status();
      whenIdle();
      return r;
    }, function (e) {
      inflight--;
      if (!util.isNetErr(e) && !e.drop) failed = true;
      status();
      whenIdle();
      throw e;
    });
  }

  /* ---------------- 发送队列 ---------------- */
  function entryPaths(e) {
    if (e.type === "end") return endPaths(e.index);
    return (e.patches || []).map(function (p) { return p.path; });
  }
  /** 结束步骤会改动的路径（开关 / 时间 / 操作人 + 阈值比较改动的检查项字段） */
  function endPaths(i) {
    var base = "main_step." + i + ".";
    var list = [base + "step_end_sw", base + "step_end_time", base + "step_end_user"];
    var ms = (store.get("main_step") || [])[i] || {};
    (ms.sub_step || []).forEach(function (s, j) {
      offline.THRESHOLD_FIELDS.forEach(function (f) { list.push(base + "sub_step." + j + "." + f); });
    });
    return list;
  }
  function stillQueued(path, except) {
    return outbox.some(function (e) { return e !== except && entryPaths(e).indexOf(path) > -1; });
  }
  /** 操作已被服务器处理：清除"本地未保存"标记（期间又改过的不清） */
  function release(e) {
    entryPaths(e).forEach(function (p) {
      if (stillQueued(p, e) || queue[p] || timers[p]) return;
      if (!e.v || e.v[p] == null || dirty[p] === e.v[p]) delete dirty[p];
    });
  }
  function count(e) { return e.type === "end" ? 1 : (e.patches || []).length; }

  function persist() {
    var list = outbox.map(function (e) {
      return {
        id: e.id, type: e.type, guard: e.guard, index: e.index, time: e.time, user: e.user, t: e.t,
        patches: e.patches && e.patches.map(function (p) { return { path: p.path, value: p.value, base: p.base }; }),
      };
    });
    var ok = offline.box.save(list, { done: list.length ? done : {} });
    if (!ok && list.length && !persistWarned) {
      persistWarned = true;
      MW.ui.toast("手机存储空间不足，离线内容可能无法保留，请尽快到有信号的位置", "warn");
    }
  }

  /** 放入队列；连续的普通保存合并为一条（同一项只保留最新值，基准保留最早的） */
  function enqueue(e) {
    e.id = e.id || util.uid();
    e.user = e.user || me();
    e.t = e.t || util.now();
    e.v = e.v || {};
    var last = outbox[outbox.length - 1];
    if (e.type === "save" && last && last.type === "save" && last !== running) {
      e.patches.forEach(function (p) {
        var old = last.patches.filter(function (x) { return x.path === p.path; })[0];
        if (old) old.value = p.value;
        else last.patches.push(p);
        last.v[p.path] = e.v[p.path];
      });
      persist();
      return last;
    }
    outbox.push(e);
    persist();
    return e;
  }
  function settle(id, ok, val) {
    var ws = waiters[id];
    if (!ws) return false;
    delete waiters[id];
    ws.forEach(function (w) { ok ? w.resolve(val) : w.reject(val); });
    return true;
  }
  /** 等待某条操作的结果；离线时立即返回 { code: 0, offline: true }（已暂存，稍后补传） */
  function wait(e) {
    return new Promise(function (resolve, reject) {
      (waiters[e.id] = waiters[e.id] || []).push({ resolve: resolve, reject: reject });
      if (offlineNow()) settle(e.id, true, { code: 0, offline: true });
    });
  }

  function send(paths, guard) {
    if (sync.stopped) return Promise.resolve({ code: 0 });
    var patches = buildPatches(paths);
    if (!patches.length) return guard ? Promise.resolve({ code: 0 }) : kick().then(function () { return { code: 0 }; });
    var e = { type: guard ? "commit" : "save", patches: patches, guard: guard || [], v: {} };
    patches.forEach(function (p) { e.v[p.path] = dirty[p.path]; });
    e = enqueue(e);
    var p = wait(e);
    kick();
    status();
    return p;
  }

  /** 启动队列发送（已在发送时返回同一个 Promise，永不 reject） */
  function kick() {
    if (draining) return draining;
    if (!outbox.length || sync.stopped) return Promise.resolve();
    clearTimeout(retryTimer);
    var fin = function () { draining = null; status(); };
    draining = step().then(fin, function (e) { console.error("[mw] 队列异常", e); fin(); });
    status();
    return draining;
  }
  function step() {
    if (sync.stopped) return Promise.resolve();
    if (!outbox.length) { finishReplay(); return Promise.resolve(); }
    var e = (running = outbox[0]);
    return run(e).then(function (r) {
      running = null;
      if (outbox[0] === e) outbox.shift();
      persist();
      release(e);
      if (wasOff && !(r && r.code === 1)) replayed += count(e);
      var mine = settle(e.id, true, r);
      if (r && r.code === 1 && !mine && !(r.rejected && r.rejected.user === e.user)) {
        var rej = r.rejected || {};
        rejectedList.push(sync.label(rej.path || entryPaths(e)[0] || "") + " 已由 " + (rej.user || "其他处理人") + (rej.time ? " 于 " + hhmm(rej.time) : "") + " 完成");
      }
      status();
      return step();
    }, function (err) {
      running = null;
      if (util.isNetErr(err)) {
        // 没网：保留队列，所有等待中的操作按"已暂存"返回，定时探测网络
        wasOff = true;
        MW.net.set(true);
        Object.keys(waiters).forEach(function (id) { settle(id, true, { code: 0, offline: true }); });
        clearTimeout(retryTimer);
        retryTimer = setTimeout(kick, PROBE_MS);
        status();
        return;
      }
      if (err && err.drop) {
        if (outbox[0] === e) outbox.shift();
        persist();
        release(e);
        if (!settle(e.id, false, err)) rejectedList.push(sync.label(entryPaths(e)[0] || "") + "：" + util.errMsg(err));
        return step();
      }
      // 服务异常：保留，稍后重试
      console.warn("[mw] 保存失败", err);
      settle(e.id, false, err);
      clearTimeout(retryTimer);
      retryTimer = setTimeout(kick, RETRY_MS);
      status();
    });
  }
  /** 队列清空：离线后补传完成时汇总提示一次，并拉一次最新数据 */
  function finishReplay() {
    if (!wasOff && !rejectedList.length && !lostPhotos) return;
    var n = replayed, rej = rejectedList, lost = lostPhotos;
    wasOff = false;
    replayed = 0;
    rejectedList = [];
    lostPhotos = 0;
    if (n) MW.ui.toast("网络已恢复，离线期间的 " + n + " 项操作已上传", "ok");
    if (rej.length) MW.ui.toast("以下离线操作未生效：" + rej.slice(0, 3).join("；") + (rej.length > 3 ? " 等 " + rej.length + " 项" : "") + "，已刷新为最新数据", "warn");
    if (lost) MW.ui.toast("有 " + lost + " 张离线照片未能上传，请重新拍摄", "warn");
    setTimeout(function () { sync.pull({ force: true }).catch(noop); }, 0);
  }

  /** 发送一条操作 */
  function run(e) {
    return resolveFiles(e).then(function () {
      if (e.type === "end") {
        return request("sync_end_step", { index: e.index, step_end_sw: true, step_end_time: e.time, operator: e.user }).then(function (r) {
          if (r.main_step) {
            release(e);
            apply({ main_step: r.main_step }, { force: true, quiet: true });
          }
          return r;
        });
      }
      var body = e.patches.map(function (p) { return { path: p.path, value: p.value, base: p.base }; });
      return request("sync_patch", { patches: body, operator: e.user, guard: e.guard || [] }).then(function (r) {
        if (r.code === 1) return r;
        e.patches.forEach(function (p) { setIn(sync.snap, p.path, util.clone(p.value)); });
        (r.conflicts || []).forEach(function (c) {
          if (c.user && c.user !== me())
            MW.ui.toast(sync.label(c.path) + " 刚被 " + c.user + (c.time ? " 于 " + hhmm(c.time) : "") + " 修改过，已保留你的填写", "warn");
        });
        return r;
      });
    });
  }

  /* ---------------- 离线照片补传 ---------------- */
  function collectFiles(v, out) {
    if (Array.isArray(v)) v.forEach(function (x) { collectFiles(x, out); });
    else if (isObj(v)) {
      if (v._local) out.push(v);
      else Object.keys(v).forEach(function (k) { collectFiles(v[k], out); });
    }
  }
  /** 原地把"待上传"照片换成已上传的正式文件项（本机已丢失的移除） */
  function swapFiles(v) {
    if (Array.isArray(v)) {
      for (var i = v.length - 1; i >= 0; i--) {
        var x = v[i];
        if (isObj(x) && x._local && x._local in done) {
          if (done[x._local]) v[i] = util.clone(done[x._local]);
          else v.splice(i, 1);
        } else swapFiles(x);
      }
    } else if (isObj(v) && !v._local) Object.keys(v).forEach(function (k) { swapFiles(v[k]); });
    return v;
  }
  function uploadLocal(it) {
    var id = it._local;
    if (id in done) return Promise.resolve();
    var lose = function () { done[id] = null; lostPhotos++; persist(); };
    return MW.files.get(id).then(function (rec) {
      if (!rec || !rec.blob) return lose();
      return api.upload(rec.blob, rec.name || it.name).then(function (resp) {
        var u = resp && resp.FileList && resp.FileList[0] ? resp.FileList[0].url : "";
        if (!u) throw new Error("照片上传失败");
        done[id] = { name: it.name || rec.name, uid: it.uid || util.uid(), status: "success", url: u, response: resp };
        persist();
        MW.files.del(id);
      }, function (err) {
        if (util.isNetErr(err)) throw err;
        console.warn("[mw] 离线照片上传失败", err);
        lose();
      });
    }, function (err) {
      if (util.isNetErr(err)) throw err;
      lose();
    });
  }
  function resolveFiles(e) {
    if (!e.patches) return Promise.resolve();
    var items = [];
    e.patches.forEach(function (p) { collectFiles(p.value, items); });
    if (!items.length) return Promise.resolve();
    return items.reduce(function (pr, it) {
      return pr.then(function () { return uploadLocal(it); });
    }, Promise.resolve()).then(function () {
      e.patches.forEach(function (p) { p.value = swapFiles(p.value); });
      persist();
      var tops = {};
      e.patches.forEach(function (p) { tops[parts(p.path)[0]] = 1; });
      Object.keys(tops).forEach(function (k) { swapFiles(store.values[k]); });
      repaintSoon(Object.keys(tops));
    });
  }
  /** 照片补传完成后刷新页面上的缩略图（输入中则稍后） */
  var repaintKeys = {}, repaintTimer = null;
  function repaintSoon(keys) {
    keys.forEach(function (k) { repaintKeys[k] = 1; });
    clearTimeout(repaintTimer);
    repaintTimer = setTimeout(function tick() {
      if (sync.stopped) return;
      var b = sync.busy();
      if (b && b !== "保存中") { repaintTimer = setTimeout(tick, 800); return; }
      var ks = Object.keys(repaintKeys);
      repaintKeys = {};
      ks.forEach(function (k) { store.set(k, store.values[k], { silent: true }); });
      if (ks.length) store.emit(ks);
    }, 300);
  }
  /** 已补传的照片（ui.upload 渲染待上传项时用） */
  sync.uploaded = function (id) { return done[id] || null; };
  /** 某张暂存照片是否仍在发送队列中（删除照片时决定是否清理本机暂存） */
  sync.fileQueued = function (id) {
    var items = [];
    outbox.forEach(function (e) { (e.patches || []).forEach(function (p) { collectFiles(p.value, items); }); });
    return items.some(function (it) { return it._local === id; });
  };

  /**
   * 开关类操作：立即保存 paths，guard 中任一路径在服务器上已为真值时整体拒绝
   * 返回 { code: 0 } / { code: 0, offline: true }（已暂存在本机） / { code: 1, rejected: {user, time} }（被拒绝时自动拉取最新数据覆盖本地）
   */
  sync.commit = function (paths, guard) {
    paths.forEach(function (p) {
      markDirty(p);
      queue[p] = {};
      clearTimeout(timers[p]);
      delete timers[p];
    });
    return send(paths, guard || []).then(function (r) {
      if (r && r.code === 1) return sync.pull({ force: true }).then(function () { return r; }, function () { return r; });
      return r;
    });
  };

  /** 点开关前：先把待保存项发出去，再强制拉一次最新数据（没网 / 失败时按本地数据继续） */
  sync.fresh = function () {
    return sync.flush().catch(noop).then(function () {
      if (offlineNow()) return;
      return sync.pull({ force: true });
    }).catch(noop);
  };
  /** 提示"xxx 已由 某人 于 几点几分 完成" */
  sync.doneBy = function (path, subject, verb, rej) {
    var w = rej && rej.user ? rej : sync.who(path);
    var t = w.time ? " 于 " + hhmm(w.time) : "";
    if (w.user && w.user === me()) MW.ui.toast(subject + " 已" + (verb || "完成") + "，已刷新为最新数据", "info");
    else MW.ui.toast(subject + " 已由 " + (w.user || "其他处理人") + t + " " + (verb || "完成") + "，已刷新为最新数据", "warn");
  };

  /** 在本机结束步骤：按与服务器相同的规则做阈值比较（联网后以服务器结果为准） */
  function localEnd(e) {
    var ms = (store.get("main_step") || [])[e.index];
    if (!ms) return;
    offline.threshold(ms.sub_step || []);
    ms.step_end_sw = true;
    ms.step_end_time = e.time;
    ms.step_end_user = e.user;
    endPaths(e.index).forEach(function (p) { markDirty(p); e.v[p] = dirty[p]; });
  }
  /** 结束步骤：本机先出结果，服务器再以最新数据做阈值比较后写入（没网时联网后补传） */
  sync.endStep = function (index, time) {
    return sync.flush().then(function () {
      var e = { type: "end", index: index, time: time, user: me(), v: {} };
      localEnd(e);
      store.set("main_step", store.get("main_step"), { silent: true });
      e = enqueue(e);
      var p = wait(e);
      kick();
      status();
      return p;
    });
  };

  /* ---------------- 拉取与三方合并 ---------------- */
  /**
   * 合并单个路径；返回 { value, changed, replaced }
   * 规则：本机未上传的项保留本地值；其余一律以服务器为准。
   * 服务器相对上次确实变了的记入 changes（提示"xxx 更新了"），服务器没变但本地不一致的记入 heals（静默纠正）
   */
  function mergeAt(path, L, S, N, out) {
    var srvChanged = !eq(N, S);
    if (eq(N, L)) return { value: L, changed: false };
    if (!srvChanged && (out.noHeal || LOCAL_DEFAULT[parts(path)[0]])) return { value: L, changed: false };
    var recurse = GRANULAR_RE.test(path) && isObj(L) && isObj(N) && Array.isArray(L) === Array.isArray(N) &&
      (!Array.isArray(N) || N.length === L.length);
    if (recurse && !dirtySelfOrAncestor(path)) {
      var changed = false;
      var keys = {};
      Object.keys(N).forEach(function (k) { keys[k] = 1; });
      if (isObj(S)) Object.keys(S).forEach(function (k) { keys[k] = 1; });
      Object.keys(keys).forEach(function (k) {
        var r = mergeAt(path + "." + k, L[k], isObj(S) ? S[k] : undefined, N[k], out);
        if (r.replaced) L[k] = r.value;
        if (r.changed) changed = true;
      });
      return { value: L, changed: changed };
    }
    if (dirtyUnder(path)) {
      if (srvChanged) out.conflicts.push(path);
      return { value: L, changed: false };
    }
    if (N === undefined) return { value: L, changed: false };
    (srvChanged ? out.changes : out.heals).push(path);
    return { value: util.clone(N), changed: true, replaced: true };
  }

  /** 把服务器数据合并进本地；只重画有变化的部分 */
  function apply(data, opt) {
    opt = opt || {};
    if (isObj(data[META])) sync.meta = data[META];
    var out = { changes: [], conflicts: [], heals: [], noHeal: MW.uploading > 0 };
    var changedKeys = [];
    Object.keys(data).forEach(function (k) {
      if (k === META || k === "$context") return;
      var N = data[k];
      if (N === null || N === undefined) return;
      var r = mergeAt(k, store.values[k], sync.snap[k], N, out);
      sync.snap[k] = util.clone(N);
      if (r.replaced) store.values[k] = r.value;
      if (r.changed) changedKeys.push(k);
    });
    lastSync = util.now();
    status();
    if (!changedKeys.length) {
      notifyConflicts(out.conflicts);
      return out;
    }
    // 只有检查清单变化时，只重画有变化的步骤卡片
    var steps = {};
    var touched = out.changes.concat(out.heals);
    touched.forEach(function (p) {
      var m = /^main_step\.(\d+)/.exec(p);
      if (m) steps[m[1]] = 1;
    });
    if (out.heals.length) console.warn("[mw] 本地数据与服务器不一致，已按服务器纠正：", out.heals.join(", "));
    var others = notifyOthers(out.changes, opt.quiet);
    var onlySteps = changedKeys.length === 1 && changedKeys[0] === "main_step" && touched.every(function (p) { return /^main_step\.\d+/.test(p); });
    sync.partial = onlySteps ? { keys: ["main_step"], steps: Object.keys(steps).map(Number), flash: others } : null;
    changedKeys.forEach(function (k) { store.set(k, store.values[k], { silent: true }); });
    store.emit(changedKeys);
    setTimeout(function () { sync.partial = null; }, 0);
    notifyConflicts(out.conflicts);
    return out;
  }

  function notifyOthers(changes, quiet) {
    var by = {}, flash = {};
    changes.forEach(function (p) {
      var w = sync.who(p);
      if (w.user && w.user === me()) return;
      var m = /^main_step\.(\d+)/.exec(p);
      if (m) flash[m[1]] = 1;
      var label = sync.label(p).replace(/^(步骤 [^.「]+)[.「].*$/, "$1");
      var u = w.user || "其他处理人";
      by[u] = by[u] || [];
      if (by[u].indexOf(label) < 0) by[u].push(label);
    });
    if (!quiet)
      Object.keys(by).forEach(function (u) {
        var ls = by[u];
        MW.ui.toast(u + " 更新了：" + ls.slice(0, 3).join("、") + (ls.length > 3 ? " 等 " + ls.length + " 处" : ""), "info");
      });
    return Object.keys(flash).map(Number);
  }
  function notifyConflicts(list) {
    var seen = {};
    list.forEach(function (p) {
      var w = sync.who(p);
      var label = sync.label(p);
      if (seen[label] || (w.user && w.user === me())) return;
      seen[label] = 1;
      MW.ui.toast(label + " 刚被 " + (w.user || "其他处理人") + (w.time ? " 于 " + hhmm(w.time) : "") + " 修改过，已保留你的填写", "warn");
    });
  }

  /**
   * 拉取最新数据：opt.force=true 时忽略忙碌状态（点开关前），否则忙碌时延后到空闲再拉
   */
  sync.pull = function (opt) {
    opt = opt || {};
    if (!sync.on || sync.stopped) return Promise.resolve(false);
    if (outbox.length) kick();
    if (!opt.force && sync.busy()) {
      wantPull = true;
      status();
      whenIdle();
      return Promise.resolve(false);
    }
    if (pulling) return pulling;
    var seq0 = seq;
    var tries = opt._tries || 0;
    pulling = api.history(sync.keys()).then(function (res) {
      pulling = null;
      var data = res && res.data;
      if (!isObj(data)) throw new Error("拉取失败");
      if (seq !== seq0 || inflight) {
        // 拉取期间自己又保存过：这份数据可能比本地旧，丢弃后重拉
        if (tries < 3) return util.delay(300).then(function () { return sync.pull(Object.assign({}, opt, { _tries: tries + 1 })); });
        wantPull = true;
        whenIdle();
        return false;
      }
      if (!opt.force && sync.busy()) {
        wantPull = true;
        whenIdle();
        return false;
      }
      wantPull = false;
      apply(data, opt);
      return true;
    }, function (e) {
      pulling = null;
      console.warn("[mw] 拉取最新数据失败", e);
      status();
      throw e;
    });
    return pulling;
  };
  sync.keys = function () {
    var k = MW.HISTORY_KEYS.slice();
    var mid = MW.biz && MW.biz.mid ? MW.biz.mid.keys() : [];
    EXTRA_KEYS.concat(mid).forEach(function (x) { if (k.indexOf(x) < 0) k.push(x); });
    return k;
  };

  /** 空闲后补一次被延后的刷新 */
  function whenIdle() {
    clearTimeout(idleTimer);
    if (!wantPull || sync.stopped) return;
    idleTimer = setTimeout(function () {
      if (!wantPull) return;
      if (sync.busy()) return whenIdle();
      sync.pull().catch(noop);
    }, 600);
  }

  /* ---------------- 同步状态条 ---------------- */
  // 固定在左上角；"已同步/已结束"这类稳定状态 3 秒后收起为小圆点图标，避免遮挡填写；状态变化或点击时展开
  var bar = null;
  var barSt = "", barMiniTimer = 0;
  function barExpand() {
    if (!bar) return;
    bar.classList.remove("is-mini");
    clearTimeout(barMiniTimer);
    barMiniTimer = setTimeout(function () {
      if (bar && (barSt === "ok" || barSt === "off")) bar.classList.add("is-mini");
    }, 3000);
  }
  function onBarClick() {
    barExpand();
    if (sync.stopped) return;
    if (outbox.length) {
      MW.ui.toast("正在尝试上传…", "info");
      kick().then(function () {
        if (outbox.length) MW.ui.toast("仍无网络，内容已暂存在本机，联网后会自动上传", "warn");
      });
      return;
    }
    sync.flush().then(function () { return sync.pull({ force: true }); }).then(function () {
      MW.ui.toast("已是最新数据", "ok");
    }, function (e) {
      MW.ui.toast(util.isNetErr(e) ? "当前网络不可用，填写内容会暂存在本机，联网后自动上传" : "同步失败，请稍后重试", "warn");
    });
  }
  function status() {
    if (!sync.on) return;
    if (!bar) bar = MW.addFloating(h("button", { type: "button", class: "mw-sync", onClick: onBarClick }, h("i"), h("span")));
    var pending = Object.keys(dirty).length;
    var n = sync.pending();
    var st, text, icon, title = "多人协同：每 15 秒自动同步他人填写的内容，点击立即同步";
    if (sync.stopped) { st = "off"; text = "工单已结束，停止同步"; icon = "ri-lock-line"; }
    else if (offlineNow()) {
      st = "offline"; icon = "ri-wifi-off-line";
      text = n ? "离线中 · 已暂存 " + n + " 项，联网后自动上传" : "离线中 · 联网后自动同步";
      title = "当前网络不可用：检查项等填写内容会暂存在本机，联网后按操作顺序自动上传；打卡、人脸识别需联网完成。点击重试";
    }
    else if (failed) { st = "error"; text = "保存失败，点击重试"; icon = "ri-error-warning-line"; }
    else if (outbox.length && wasOff) { st = "saving"; text = "正在上传离线暂存的 " + n + " 项…"; icon = "ri-loader-4-line mw-spin"; }
    else if (inflight || (outbox.length && draining)) { st = "saving"; text = "保存中…"; icon = "ri-loader-4-line mw-spin"; }
    else if (outbox.length) { st = "hold"; text = "待上传 " + n + " 项，点击重试"; icon = "ri-upload-cloud-2-line"; }
    else if (pending && Object.keys(timers).length) { st = "hold"; text = "输入中，停止输入后自动保存"; icon = "ri-keyboard-line"; }
    else if (pending) { st = "saving"; text = "保存中…"; icon = "ri-loader-4-line mw-spin"; }
    else if (wantPull) { st = "hold"; text = sync.busy() ? sync.busy() + "，稍后刷新" : "等待刷新"; icon = "ri-keyboard-line"; }
    else { st = "ok"; text = "已同步 " + (lastSync ? lastSync.slice(11, 19) : ""); icon = "ri-download-cloud-2-line"; }
    var mini = bar.classList.contains("is-mini");
    bar.className = "mw-sync mw-scope is-" + st + (mini && st === barSt ? " is-mini" : "");
    bar.title = title;
    bar.setAttribute("aria-label", text);
    bar.firstChild.className = icon;
    bar.lastChild.textContent = text;
    if (st !== barSt) { barSt = st; barExpand(); }
  }
  sync.status = status;

  /* ---------------- 网络状态 ---------------- */
  function onNet(isOff) {
    if (!sync.on) return;
    status();
    if (isOff) {
      if (Date.now() - offToastAt > 60000) {
        offToastAt = Date.now();
        MW.ui.toast("网络已断开：检查项等填写内容会暂存在本机，联网后自动上传", "warn");
      }
      if (outbox.length && !draining) {
        clearTimeout(retryTimer);
        retryTimer = setTimeout(kick, PROBE_MS);
      }
    } else if (outbox.length) kick();
  }
  function onOnline() {
    if (outbox.length) kick();
    else sync.pull({ force: true }).catch(noop);
  }
  function onOffline() { MW.net.set(true); }

  /* ---------------- 生命周期 ---------------- */
  function onVisible() {
    if (document.visibilityState === "visible") {
      if (outbox.length) kick();
      sync.pull().catch(noop);
    } else sync.flush().catch(noop);
  }
  function onCompStart() { composing = true; }
  function onCompEnd() { composing = false; lastInput = Date.now(); whenIdle(); }
  function onFocusOut() { setTimeout(whenIdle, 50); }
  function onWinFocus() { if (pickUntil) pickUntil = Math.min(pickUntil, Date.now() + 1500); whenIdle(); }
  function onKey(e) { if (inScope(e.target)) lastInput = Date.now(); }
  function onPageHide() { sync.flush().catch(noop); }

  function stop(msg) {
    if (sync.stopped) return;
    sync.stopped = true;
    clearInterval(pollTimer);
    clearTimeout(retryTimer);
    var n = sync.pending();
    outbox = [];
    offline.box.clear();
    status();
    if (msg) MW.ui.toast(msg + (n ? "，本机暂存的 " + n + " 项未能上传" : ""), "warn");
  }

  /** 打开页面时恢复本机暂存的离线操作（上次没网时填写、还没上传的内容） */
  function restore() {
    var d = offline.box.load();
    if (!d) return;
    if (!d.list.length) return offline.box.clear();
    done = d.done || {};
    var tops = {};
    d.list.forEach(function (x) {
      var e = { id: x.id, type: x.type, patches: x.patches || null, guard: x.guard || [], index: x.index, time: x.time, user: x.user, t: x.t, v: {} };
      if (e.type === "end") {
        localEnd(e);
        tops.main_step = 1;
      } else {
        (e.patches || []).forEach(function (p) {
          setIn(store.values, p.path, util.clone(p.value));
          markDirty(p.path);
          e.v[p.path] = dirty[p.path];
          tops[parts(p.path)[0]] = 1;
        });
      }
      outbox.push(e);
    });
    wasOff = true;
    var keys = Object.keys(tops);
    keys.forEach(function (k) { store.set(k, store.values[k], { silent: true }); });
    if (keys.length) store.emit(keys);
    MW.ui.toast("已恢复本机暂存的 " + sync.pending() + " 项离线操作" + (offlineNow() ? "，联网后自动上传" : "，正在上传"), "info");
  }

  /** MW.start 回填完成后调用：data 为首次 GetHistoryVariables 的结果（作为合并基准） */
  sync.start = function (data) {
    if (sync.on) return;
    var ctx = store.get("$context") || {};
    if (!ctx.instanceId) return;
    if (closed()) return offline.box.clear();
    sync.on = true;
    Object.keys(data || {}).forEach(function (k) {
      if (k === META) return;
      if (data[k] !== null && data[k] !== undefined) sync.snap[k] = util.clone(data[k]);
    });
    if (isObj(data && data[META])) sync.meta = data[META];
    lastSync = util.now();
    restore();
    pollTimer = setInterval(function () {
      if (document.visibilityState !== "visible") return;
      if (closed()) return stop();
      if (outbox.length) kick();
      else sync.pull().catch(noop);
    }, POLL_MS);
    var unNet = MW.net.on(onNet);
    document.addEventListener("visibilitychange", onVisible);
    document.addEventListener("compositionstart", onCompStart, true);
    document.addEventListener("compositionend", onCompEnd, true);
    document.addEventListener("focusout", onFocusOut, true);
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("focus", onWinFocus);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    status();
    if (outbox.length) kick();
    MW.cleanups.push(function () {
      clearInterval(pollTimer);
      clearTimeout(idleTimer);
      clearTimeout(retryTimer);
      clearTimeout(repaintTimer);
      unNet();
      document.removeEventListener("visibilitychange", onVisible);
      document.removeEventListener("compositionstart", onCompStart, true);
      document.removeEventListener("compositionend", onCompEnd, true);
      document.removeEventListener("focusout", onFocusOut, true);
      document.removeEventListener("keydown", onKey, true);
      window.removeEventListener("focus", onWinFocus);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      bar = null;
      sync.on = false;
    });
  };

  /** 测试用：暴露合并函数 */
  sync._merge = function (L, S, N, dirtyPaths) {
    var saved = dirty;
    dirty = {};
    (dirtyPaths || []).forEach(function (p) { dirty[p] = 1; });
    var out = { changes: [], conflicts: [], heals: [] };
    var r = mergeAt("main_step", L, S, N, out);
    dirty = saved;
    return { value: r.value, changes: out.changes, conflicts: out.conflicts, heals: out.heals };
  };
  /** 测试用：查看发送队列 */
  sync._outbox = function () { return outbox; };
}
